import {
  aabbFromPoints,
  equals,
  pointInPolygon,
  polygonCentroid,
  rectanglePolygon,
  type Vec2,
} from '@indusketch/geometry';
import {
  buildingArea,
  buildingCorners,
  DEFAULT_WALL_THICKNESS,
  ownedWallId,
  rectangleWallGeometry,
  type RectPlacement,
} from './building.js';
import { axisAlignedRectangle } from './site.js';
import type { ProjectDocument, Room, RoomType, Wall, WallCladding } from './types.js';
import { inEveryLocale, modelTexts } from './texts.js';

export const DEFAULT_INTERIOR_WALL_THICKNESS = 0.15;
export const DEFAULT_WALL_COLOR = '#d5d9df';
/** Drywall partition (metal stud, 2 × 12.5 mm boards): 12.5 cm in total. */
export const DRYWALL_THICKNESS = 0.125;

/** `masonry-*` are sand-lime brick walls (Kalksandstein). */
export type WallTypeId =
  | 'drywall'
  | 'masonry-115'
  | 'masonry-175'
  | 'masonry-24'
  | 'concrete-20'
  | 'concrete-25'
  | 'brick-24'
  | 'sandwich'
  | 'sandwich-horizontal';

export interface WallTypeInfo {
  readonly id: WallTypeId;
  readonly label: string;
  readonly thickness: number;
  readonly cladding: WallCladding;
  readonly color: string;
}

const SAND_LIME_COLOR = '#e4e1d8';
const CONCRETE_COLOR = '#b4b4ae';
const BRICK_COLOR = '#9a4130';

const wallType = (
  id: WallTypeId,
  thickness: number,
  cladding: WallCladding = 'plain',
  color = DEFAULT_WALL_COLOR,
): WallTypeInfo => ({
  id,
  get label() {
    return modelTexts().wallTypes[id];
  },
  thickness,
  cladding,
  color,
});

/** Wall build-ups offered when drawing interior walls and rooms, and to switch a wall to. */
export const WALL_TYPES: readonly WallTypeInfo[] = [
  wallType('drywall', DRYWALL_THICKNESS),
  wallType('masonry-115', 0.115, 'sand-lime', SAND_LIME_COLOR),
  wallType('masonry-175', 0.175, 'sand-lime', SAND_LIME_COLOR),
  wallType('masonry-24', 0.24, 'sand-lime', SAND_LIME_COLOR),
  wallType('concrete-20', 0.2, 'concrete', CONCRETE_COLOR),
  wallType('concrete-25', 0.25, 'concrete', CONCRETE_COLOR),
  wallType('brick-24', 0.24, 'brick', BRICK_COLOR),
  // Same panel as the default hall shell.
  wallType('sandwich', DEFAULT_WALL_THICKNESS, 'sandwich'),
  wallType('sandwich-horizontal', DEFAULT_WALL_THICKNESS, 'sandwich-horizontal'),
];

/** The wall type a wall is built as (same thickness and surface), or null for its own mix. */
export const wallTypeOf = (w: Pick<Wall, 'thickness' | 'cladding'>): WallTypeInfo | null =>
  WALL_TYPES.find((t) => Math.abs(t.thickness - w.thickness) < 1e-6 && t.cladding === w.cladding) ??
  null;

export const wallTypeInfo = (id: WallTypeId): WallTypeInfo =>
  WALL_TYPES.find((t) => t.id === id) ?? wallType('drywall', DRYWALL_THICKNESS);

export interface RoomTypeInfo {
  readonly id: RoomType;
  readonly label: string;
  readonly color: string;
}

const roomType = (id: RoomType, color: string): RoomTypeInfo => ({
  id,
  get label() {
    return modelTexts().roomTypes[id];
  },
  color,
});

export const ROOM_TYPES: readonly RoomTypeInfo[] = [
  roomType('office', '#e6edf8'),
  roomType('meeting', '#eee6f7'),
  roomType('kitchen', '#fbeedb'),
  roomType('wc-women', '#e0f1f3'),
  roomType('wc-men', '#e0f1f3'),
  roomType('wc-accessible', '#d8edf1'),
  roomType('changing', '#eaf3e0'),
  roomType('shower', '#dcedf6'),
  roomType('corridor', '#f2f1ec'),
  roomType('technical', '#e9e9e9'),
  roomType('storage', '#f2eadc'),
  roomType('other', '#f3efe6'),
];

export const roomTypeInfo = (id: RoomType): RoomTypeInfo =>
  ROOM_TYPES.find((t) => t.id === id) ?? roomType(id, DEFAULT_ROOM_COLOR);

/** True if `name` is the room type's default name in any language. */
export const isDefaultRoomName = (type: RoomType, name: string): boolean =>
  inEveryLocale((t) => t.roomTypes[type]).includes(name);

/**
 * Whether wall `w` would double an existing wall: parallel, touching or overlapping in
 * thickness and covering at least 80 % of `w`'s length. Rooms drawn against a neighbour or
 * the building shell then share that wall instead of standing wall to wall.
 */
export function duplicatesWall(
  existing: readonly Wall[],
  w: Pick<Wall, 'start' | 'end' | 'thickness'>,
): boolean {
  const len = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y);
  if (len < 1e-6) return false;
  const dir = { x: (w.end.x - w.start.x) / len, y: (w.end.y - w.start.y) / len };
  return existing.some((e) => {
    const el = Math.hypot(e.end.x - e.start.x, e.end.y - e.start.y);
    if (el < 1e-6) return false;
    const ed = { x: (e.end.x - e.start.x) / el, y: (e.end.y - e.start.y) / el };
    if (Math.abs(dir.x * ed.x + dir.y * ed.y) < 0.999) return false;
    // Perpendicular distance between the two centerlines.
    const gap = Math.abs((w.start.x - e.start.x) * -dir.y + (w.start.y - e.start.y) * dir.x);
    if (gap > (w.thickness + e.thickness) / 2 + 0.02) return false;
    const t = (p: Vec2) => (p.x - w.start.x) * dir.x + (p.y - w.start.y) * dir.y;
    const [e0, e1] = [t(e.start), t(e.end)].sort((a, b) => a - b) as [number, number];
    return Math.min(len, e1) - Math.max(0, e0) >= 0.8 * len;
  });
}
export const DEFAULT_INTERIOR_WALL_HEIGHT = 3;
export const DEFAULT_ROOM_COLOR = '#f3efe6';
/** Endpoints closer than this are considered connected (1 mm). */
export const CONNECT_TOLERANCE = 1e-3;

type BuildingsOnly = Pick<ProjectDocument, 'buildings'>;

/**
 * The building whose footprint contains the point, used to attach zones, rooms and walls. An
 * office built into a hall wins over the hall, so its rooms move with the office.
 */
export function findBuildingAt(doc: BuildingsOnly, p: Vec2): string | null {
  let best: { id: string; area: number } | null = null;
  for (const b of Object.values(doc.buildings)) {
    if (!pointInPolygon(p, buildingCorners(b))) continue;
    const area = buildingArea(b);
    if (!best || area < best.area) best = { id: b.id, area };
  }
  return best?.id ?? null;
}

export function findBuildingForPolygon(
  doc: BuildingsOnly,
  polygon: readonly Vec2[],
): string | null {
  return findBuildingAt(doc, polygonCentroid(polygon));
}

export interface RoomRect {
  readonly origin: Vec2;
  readonly width: number;
  readonly length: number;
}

/** Rooms are axis-aligned rectangles in this version; returns null for other shapes. */
export function roomRect(room: Pick<Room, 'polygon'>): RoomRect | null {
  return axisAlignedRectangle(room.polygon);
}

export function rectToPlacement(r: RoomRect): RectPlacement {
  return {
    center: { x: r.origin.x + r.width / 2, y: r.origin.y + r.length / 2 },
    width: r.width,
    depth: r.length,
    rotation: 0,
  };
}

export function roomPolygon(r: RoomRect): Vec2[] {
  return rectanglePolygon(r.width, r.length, r.origin);
}

/**
 * Wall centerlines of a rectangular room, side order south, east, north, west. South and
 * north span the full width; east and west fit in between – unless the south or north wall
 * is missing (shared with a neighbour), then they run through to the room edge so the
 * corner closes against the neighbour's wall.
 */
export function roomWallGeometry(
  rect: RoomRect,
  t: number,
  present: readonly boolean[],
): { start: Vec2; end: Vec2 }[] {
  const x0 = rect.origin.x;
  const y0 = rect.origin.y;
  const x1 = x0 + rect.width;
  const y1 = y0 + rect.length;
  const bottom = present[0] === false ? y0 : y0 + t;
  const top = present[2] === false ? y1 : y1 - t;
  return [
    { start: { x: x0, y: y0 + t / 2 }, end: { x: x1, y: y0 + t / 2 } },
    { start: { x: x1 - t / 2, y: bottom }, end: { x: x1 - t / 2, y: top } },
    { start: { x: x1, y: y1 - t / 2 }, end: { x: x0, y: y1 - t / 2 } },
    { start: { x: x0 + t / 2, y: top }, end: { x: x0 + t / 2, y: bottom } },
  ];
}

export function createRoomWalls(
  room: Room,
  rect: RoomRect,
  thickness: number,
  cladding: WallCladding = 'plain',
): Wall[] {
  return rectangleWallGeometry(rectToPlacement(rect), thickness).map((g, i) => ({
    id: ownedWallId(room.id, i),
    start: g.start,
    end: g.end,
    thickness,
    height: room.height,
    baseElevation: 0,
    level: room.level,
    kind: 'interior',
    cladding,
    ownerId: room.id,
    color: DEFAULT_WALL_COLOR,
    locked: false,
  }));
}

/** Normalizes a dragged rectangle (any corner order) into origin + positive size. */
export function rectFromCorners(a: Vec2, b: Vec2): RoomRect {
  const box = aabbFromPoints([a, b]);
  if (!box) return { origin: a, width: 0, length: 0 };
  return { origin: box.min, width: box.max.x - box.min.x, length: box.max.y - box.min.y };
}

export const pointsConnected = (a: Vec2, b: Vec2): boolean => equals(a, b, CONNECT_TOLERANCE);
